import { cloneDeep, first, last, flatMap, isEqual, isEqualWith, sortBy, isEmpty, min, max } from 'lodash'
import moment from 'moment'
import { v4 as uuidV4 } from 'uuid'
import produce from 'immer'
import { MOVE_VERTICAL, MOVE_CENTER, MOVE_HORIZONTAL, MOVE_NONE } from '@teqplay/viewer'

import { VISIT_STATUSES, QUAY_ROLES } from '../js/constants'
import {
  findMatchingProduct,
  getAllProductsAtQuays,
  getSlowestLoadStationOfProduct,
  isNumber,
  getLoadstationAtBerth,
  roundToMinutes
} from './general'
import {
  calculateExtraNonWorkingTime,
  getNonWorkingHoursTimeFrames,
  moveTimeIfInNonWorkingTime
} from './nonWorkingHoursUtils'
import { partlyOverlappingOrdersInTime, getVisitStartTime, getVisitEndTime } from './terminalVisitConflictsUtils'
import { sendMessageToSentry } from '../js/sentry'

/**
 * Determine the berth based on the bollards
 */
export function determineBerthName(berths, startBollard, endBollard) {
  const berthsInUse = determineBerthsInUse(berths, startBollard, endBollard)

  return berthsInUse.map(berth => berth.terminalName).join(', ')
}

/**
 * Determine the berth based on the bollards
 */
export function determineBerthsInUse(berths = [], startBollard, endBollard) {
  return berths.filter(berth => berth && startBollard < berth.endBollard && endBollard > berth.startBollard)
}

/**
 * Determine the duration based on product and amount of the product amount
 */
export function determineDuration(product, amount, operation, productInformation) {
  // Find a matching product and get the slowest product
  const matchingProduct = findMatchingProduct(product, operation, productInformation)

  if (!matchingProduct || !matchingProduct.loadSpeed) {
    return 0
  }

  // calculate the amount of hours it would take to load the product and set it as endtime
  const amountOfHours = amount ? amount / matchingProduct.loadSpeed : 0
  return Math.ceil(3600000 * amountOfHours)
}

/**
 * Determine the ship length based on product and the product
 * @param {string} productName
 * @param {Config} config *
 */
export function determineShipLength(productName, config) {
  const loadStation = getSlowestLoadStationOfProduct(config.quays, productName)
  console.log('determineShipLength', { loadStation })

  return loadStation
    ? Math.round((loadStation.bollardDefaultEnd - loadStation.bollardDefaultStart) * config.bollardLength)
    : undefined
}

/**
 * Determine the start bollard based on product
 */
export function determineStartBollard(endBollard, shipLength, quayInformation) {
  // Math floor is used to round down the answer
  return Math.floor(endBollard - shipLength / quayInformation.bollardLength)
}

/**
 * Determine the end bollard based on product
 */
export function determineEndBollard(startBollard, shipLength, quayInformation) {
  // Math ceil is used to round up the answer
  return Math.ceil(startBollard + shipLength / quayInformation.bollardLength)
}

/**
 * Get the Minimum and Maximum Bollards of quays with type normal.
 * @param {Quay[]} quays
 */
export function getMinMaxBollards(quays) {
  const normalQuays = quays.filter(quay => quay.role === QUAY_ROLES.normal)
  if (normalQuays.length) {
    const minBollard = Math.min(...normalQuays.map(quay => quay.minBollard))
    const maxBollard = Math.max(...normalQuays.map(quay => quay.maxBollard))

    return {
      minBollard,
      maxBollard
    }
  }

  // Every terminal is expected to have at least one Quay with role 'normal'
  // Send message to sentry if this is not the case
  sendMessageToSentry('No quays found with role normal, fix this for this terminal', {
    functionName: 'getMinMaxBollards',
    quays
  })
  return {
    minBollard: 0,
    maxBollard: 0
  }
}

/**
 * Check whether the cargo start/end is currently chained
 * @param {Order} order
 * @param {Config} config
 * @param {number} totalDurationDelays
 * @returns {boolean}
 */
export function checkChainCargo(order, config, totalDurationDelays) {
  const updatedOrder = cloneDeep(order)

  updateCargoEnd(updatedOrder, config, totalDurationDelays)

  return isEqual(order, updatedOrder)
}

/**
 * Check whether the bollard start/end are currently chained
 * @param {Order} order
 * @param {TerminalVisitShip} ship
 * @param {Config} config
 * @returns {boolean}
 */
export function checkChainBollard(order, ship, config) {
  const updatedOrder = cloneDeep(order)

  if (!order.bollardEnd && !order.bollardStart) {
    // Set as linked if not set yet
    return true
  }

  const { bollardStart, bollardEnd } = calculateOptimalBollardPositions(ship, config, updatedOrder)
  const differenceOptimal = bollardEnd - bollardStart
  const differenceReality = order.bollardEnd - order.bollardStart

  return isEqual(differenceOptimal, differenceReality)
}

/**
 * Determine slacktime based on product
 * @param product
 * @param operation
 * @param ship
 * @param products
 */
export function determineSlackTime(product, operation, ship, products) {
  if (!product || !ship || !ship.type || !ship.length) {
    return { prior: 0, after: 0 }
  }

  // Find a matching product and get the slowest product
  const matchingProduct = findMatchingProduct(product, operation, products)

  if (!matchingProduct) {
    return { prior: 0, after: 0 }
  }

  if (ship.type === 'barge') {
    // barge
    return matchingProduct.slackTimes.barge
  } else {
    if (ship.length >= 100) {
      // deepsea ship
      return matchingProduct.slackTimes.ds
    } else {
      // short sea ship
      return matchingProduct.slackTimes.ss
    }
  }
}

/**
 * Calculate the total duration of delays of a visit
 * @param {TerminalVisit} terminalVisit
 * @returns {number} Returns the total duration in milliseconds
 */
export function determineTotalDurationDelays(terminalVisit) {
  // If the ship is moored or sailed take the delays into account when calculating the cargo end time
  if ((terminalVisit.status === 'moored' || terminalVisit.status === 'sailed') && terminalVisit.orders.length > 0) {
    return terminalVisit.delays
      .filter(delay => typeof delay.startTime === 'number' && typeof delay.endTime === 'number')
      .map(delay => delay.endTime - delay.startTime)
      .reduce((a, b) => a + b, 0)
  }

  return 0
}

// TODO: rewrite to applyNonWorkingHours, or two separate functions applyNonWorkingHoursToCargoEnd and applyNonWorkingHoursToCargoStart?
export function calculateTotalNonWorkingHoursDelays(startTime, endTime, nonWorkingHours) {
  let nonWorkingTime = 0

  if (nonWorkingHours && nonWorkingHours.length) {
    const toCheckNonWorkingHours = nonWorkingHours.filter(nwh => nwh.affectTerminalVisits)
    const nonWorkingHourTimeframes = toCheckNonWorkingHours.map(nwh =>
      getNonWorkingHoursTimeFrames(startTime, endTime, nwh)
    )

    // Check which non working hour timeframes are actually during the terminal visit
    nonWorkingHourTimeframes.forEach(timeFramePerNonWorkingHours => {
      const relevantTimeFrames = timeFramePerNonWorkingHours.filter(
        tfpnwh => tfpnwh.endTime >= startTime && endTime >= tfpnwh.startTime
      )

      // Check how much time is
      relevantTimeFrames.forEach(rtf => (nonWorkingTime += calculateExtraNonWorkingTime(rtf, startTime, endTime)))
    })

    let calculatedEndTime = endTime + nonWorkingTime

    // Check if endTime falls into new non working hours block
    let additionalTimeToMove = moveTimeIfInNonWorkingTime(calculatedEndTime, toCheckNonWorkingHours)

    // Keep on updating the non working time if it keeps on falling into non working hours
    while (additionalTimeToMove !== 0) {
      nonWorkingTime += additionalTimeToMove
      calculatedEndTime = endTime + nonWorkingTime
      additionalTimeToMove = moveTimeIfInNonWorkingTime(calculatedEndTime, toCheckNonWorkingHours)
    }

    return nonWorkingTime
  }

  return 0
}

/**
 * Update cargoEnd based on cargoStart and product and amount
 * @param {Order} order
 * @param {Config} config
 * @param {number} totalDurationDelays
 * @returns {Order | null}
 */
export function updateCargoEnd(order, config, totalDurationDelays) {
  const canUpdateCargoEnd = order.cargoStart && order.product
  if (!canUpdateCargoEnd) {
    return null
  }

  if (typeof order.cargoStart === 'number') {
    const durationOperation = determineDuration(
      order.product,
      order.amount,
      order.operation,
      getAllProductsAtQuays(config.quays)
    )

    const endTime =
      order.cargoStart + (order.slackPrior || 0) + durationOperation + totalDurationDelays + (order.slackAfter || 0)

    const durationNonWorkingHours = calculateTotalNonWorkingHoursDelays(
      order.cargoStart,
      endTime,
      config.nonWorkingHours
    )

    order.cargoEnd = endTime + durationNonWorkingHours
  }

  return order
}

/**
 * Update cargoStart based on cargoEnd and product and amount
 * @param {Order} order
 * @param {Config} config
 * @param {number} totalDurationDelays
 */
// TODO: can we combine or merge or reuse logic from updateCargoStart and updateCargoEnd? They are quite similar
export function updateCargoStart(order, config, totalDurationDelays) {
  const canUpdateCargoStart = order.cargoEnd && order.product && order.amount
  if (!canUpdateCargoStart) {
    return
  }

  if (typeof order.cargoEnd === 'number') {
    const durationOperation = determineDuration(
      order.product,
      order.amount,
      order.operation,
      getAllProductsAtQuays(config.quays)
    )

    const duration = (order.slackPrior || 0) + durationOperation + totalDurationDelays + (order.slackAfter || 0)
    const startTime = order.cargoEnd - duration

    const durationNonWorkingHours = calculateTotalNonWorkingHoursDelays(
      startTime,
      order.cargoEnd,
      config.nonWorkingHours
    )

    order.cargoStart = startTime - durationNonWorkingHours
  }
}

/**
 * Update the start bollard based on end bollard and ship length
 * @param {Order} order
 * @param {TerminalVisitShip} ship
 * @param {Config} config
 */
export function updateBollardStart(order, ship, config) {
  if (isNumber(order.bollardEnd) && ship.length) {
    order.bollardStart = determineStartBollard(order.bollardEnd, ship.length, config)
  }

  return order
}

/**
 * Update the end bollard based on start bollard and ship length
 * @param {Order} order
 * @param {TerminalVisitShip} ship
 * @param {Config} config
 */
export function updateBollardEnd(order, ship, config) {
  if (isNumber(order.bollardStart) && ship.length) {
    order.bollardEnd = determineEndBollard(order.bollardStart, ship.length, config)
  }
  return order
}

/**
 *
 * @param {TerminalVisitShip} ship
 * @param {Config} config
 * @param {Order} order
 * @param {Quay} [quay]
 * @param {TerminalPlannerBerth} [berth]
 * @return {{bollardStart: number, bollardEnd: number }}
 */
export function calculateOptimalBollardPositions(ship, config, order, quay, berth) {
  const shipLength = ship.length ? ship.length : determineShipLength(order.product, config)

  let bollardStart = order.bollardStart || 0
  let bollardEnd = order.bollardEnd || 0

  console.log({ shipLength })

  if (!shipLength) {
    return {
      bollardStart,
      bollardEnd
    }
  }

  const quayList = quay ? [quay] : config.quays
  const loadStation = berth
    ? getLoadstationAtBerth(berth, quayList, order.product)
    : getSlowestLoadStationOfProduct(quayList, order.product)

  console.log({ loadStation })

  if (loadStation) {
    const lockBollardStart = !loadStation.reverseStartPlacement
    if (lockBollardStart) {
      // @ts-ignore Fix when having typescript to check if it's mobru or jetty
      bollardStart = Math.floor(loadStation.bollardStart || loadStation.bollard || loadStation.bollardDefaultStart)
      bollardEnd = determineEndBollard(bollardStart, shipLength, config)
    } else {
      // @ts-ignore
      bollardEnd = Math.ceil(loadStation.bollardStart || loadStation.bollard || loadStation.bollardDefaultStart)
      bollardStart = determineStartBollard(bollardEnd, shipLength, config)
      if (bollardStart < 0) {
        console.log('lower than 000')
      }
    }
  } else {
    bollardStart = berth ? berth.startBollard : 0
    bollardEnd = determineEndBollard(bollardStart, shipLength, config)
  }

  console.log({ bollardStart, bollardEnd, shipLength })

  const { minBollard, maxBollard } = getMinMaxBollards(config.quays)

  // If bollard end is higher than the max, set it to max
  if (bollardEnd > maxBollard) {
    bollardEnd = maxBollard

    const newStartBasedOnNewEnd = determineStartBollard(bollardEnd, shipLength, config)
    // if new start bollard is higher than the min, set it
    if (newStartBasedOnNewEnd >= minBollard) {
      bollardStart = newStartBasedOnNewEnd
    }
  }

  // If bollard start is lower than min, set it to min
  if (bollardStart < minBollard) {
    bollardStart = minBollard

    const newEndBasedOnNewStart = determineEndBollard(bollardStart, shipLength, config)
    if (newEndBasedOnNewStart <= maxBollard) {
      bollardEnd = newEndBasedOnNewStart
    }
  }

  return {
    bollardStart,
    bollardEnd
  }
}

/**
 * @param {number} bollardStart
 * @param {number} bollardEnd
 * @param {Quay[]} quayList
 * @return {Quay | undefined}
 */
export function getClosestQuayToBollard(bollardStart, bollardEnd, quayList) {
  const bollardsMiddle = (bollardEnd - bollardStart) / 2 + bollardStart

  const closestQuay = produce(quayList, quays => {
    return quays
      .filter(quay => quay.role === QUAY_ROLES.normal)
      .filter(quay => quay.minBollard <= bollardsMiddle && quay.maxBollard >= bollardsMiddle)
      .sort((a, b) => a.maxBollard - a.minBollard - (b.maxBollard - b.minBollard))[0]
  })
  if (!closestQuay) {
    return undefined
  }
  return closestQuay
}

/**
 * @param {Array} quayList
 * @param {TerminalVisit} visit
 * @return {boolean}
 */
export function isVisitInNormalQuay(quayList, visit) {
  const normalQuays = quayList.filter(quay => quay.role === QUAY_ROLES.normal)
  const findNormalQuay = normalQuays.find(quay => quay.name === visit.quayId)
  return findNormalQuay ? true : false
}

/**
 * When the ship is not assigned, update it's length and type based
 * on the defaults configured for this product
 * @param {TerminalVisit} terminalVisit
 * @param {Config} config
 */
export function updateShipLength(terminalVisit, config) {
  // in case of multiple orders, we simply take the default ship length
  // based on the product of the first order even though this is a bit arbitrary
  if (terminalVisit.ship && !terminalVisit.ship.mmsi && !terminalVisit.ship.length) {
    const order = first(terminalVisit.orders)
    const length = determineShipLength(order.product, config)

    if (typeof length === 'number') {
      terminalVisit.ship.length = length
    }
  }

  return terminalVisit
}

/**
 * Update the default values for slack based on the selected product
 * @param {Order} order
 * @param {TerminalVisitShip} ship
 * @param {Config} config
 */
export function updateDefaultSlack(order, ship, config) {
  if (order.product) {
    const slackTimes = determineSlackTime(order.product, order.operation, ship, getAllProductsAtQuays(config.quays))

    // update order slackPrior and slackAfter if not already set
    if (typeof order.slackPrior !== 'number' || order.slackPrior === 0) {
      order = { ...order, slackPrior: hoursToMillis(slackTimes.prior) }
    }
    if (typeof order.slackAfter !== 'number' || order.slackAfter === 0) {
      order = { ...order, slackAfter: hoursToMillis(slackTimes.after) }
    }
  }

  return order
}

/**
 * Sync the bollards and slack of all orders based on the provided order
 * @param {TerminalVisit} terminalVisit
 * @param {Order} order
 */
export function syncParallelOrders(terminalVisit, order) {
  if (!isNumber(order.bollardStart) || !isNumber(order.bollardEnd)) {
    return
  }

  terminalVisit.orders.forEach(o => {
    o.bollardStart = order.bollardStart
    o.bollardEnd = order.bollardEnd
  })
}

/**
 * Determine the order number (index+1) of an order by it's id.
 * The orders are sorted by cargoStart, and then the index+1 is returned.
 *
 * @param {TerminalVisit} terminalVisit
 * @param {string} orderId
 * @returns {number | undefined}
 */
export function determineOrderNumber(terminalVisit, orderId) {
  if (terminalVisit.orders.length === 1) {
    return 1
  }

  const sortedOrders = getSortedOrders(terminalVisit)

  const index = sortedOrders.findIndex(order => order.uuid === orderId)
  if (index === -1) {
    return undefined
  }

  return index + 1
}

/**
 * Test whether a visit has multiple orders
 * @param {TerminalVisit} terminalVisit
 * @return {boolean}
 */
export function hasMultipleOrders(terminalVisit) {
  return terminalVisit.orders.length > 1
}

/**
 * Test whether a visit contains parallel orders.
 * This is the case when every order is parallel with at least one other order
 * @param {TerminalVisit} terminalVisit
 * @return {boolean}
 */
export function hasParallelOrders(terminalVisit) {
  // optimization: no need to do anything if there is only one order
  if (terminalVisit.orders.length <= 1) {
    return false
  }

  return terminalVisit.orders.every(currentOrder => {
    return terminalVisit.orders
      .filter(order => order !== currentOrder) // do not compare against itself!
      .some(order => isParallel(currentOrder, order))
  })
}

/**
 * Returns true when the visit is completed: the ship sailed
 * @param {TerminalVisit} terminalVisit
 * @return {boolean}
 */
export function visitIsCompleted(terminalVisit) {
  return terminalVisit.status === VISIT_STATUSES.sailed
}

/**
 * Returns the permitted movement when the visit is completed: the ship sailed
 * @param {TerminalVisit} terminalVisit
 * @param {boolean} isPlanner
 * @return {number}
 */
export function getMovementOptions(terminalVisit, isPlanner) {
  if (!isPlanner) {
    return MOVE_NONE
  }
  return visitIsCompleted(terminalVisit) ? MOVE_HORIZONTAL : MOVE_VERTICAL | MOVE_CENTER
}

/**
 * Returns true when the visit is currently taking place: the ship is moored
 * @param {TerminalVisit} terminalVisit
 * @return {boolean}
 */
export function visitIsTakingPlace(terminalVisit) {
  return terminalVisit.status === VISIT_STATUSES.moored
}

/**
 * Test whether two orders are parallel.
 * This is the case when they have the same bollards
 * and are (partly) overlapping in time
 * @param {object} order1
 * @param {object} order2
 */
export function isParallel(order1, order2) {
  return (
    order1.bollardStart === order2.bollardStart &&
    order1.bollardEnd === order2.bollardEnd &&
    partlyOverlappingOrdersInTime(order1, order2)
  )
}

/**
 * Returns the orders from the termnalVisit sorted by cargoStart and cargoEnd
 * @param {TerminalVisit} terminalVisit
 * @returns {Order[]}
 */
export function getSortedOrders(terminalVisit) {
  return sortBy(terminalVisit.orders, ['cargoStart', 'cargoEnd'])
}

/**
 * For every order in every terminal visit, create a pair { visit, order }
 * @param {TerminalVisit[]} terminalVisits
 * @returns {Array.<{visit: TerminalVisit, order: Order}>}
 */
export function createVisitOrderPairs(terminalVisits) {
  const flatMapVisits = flatMap(terminalVisits, visit => {
    return visit.orders.map(order => ({ visit, order }))
  })
  return sortBy(flatMapVisits, ['order.cargoStart', 'visit._id'])
}

/**
 * Clear auto generated values: eta, ata, atd, ...
 * @param {TerminalVisit} terminalVisit
 */
export function clearAtaEtaAtd(terminalVisit) {
  terminalVisit.etaTime = undefined
  terminalVisit.etaTimeUpdatedAt = undefined
  terminalVisit.ataTime = undefined
  terminalVisit.atdTime = undefined
}

/**
 * Convert a duration in hours to milliseconds
 * @param {number} hours
 * @returns {number}
 */
export function hoursToMillis(hours) {
  return hours * 1000 * 60 * 60
}

export function getDataRange(history, dataRange) {
  if (history && history.location && history.location.pathname && history.location.pathname.includes('/coordinator')) {
    return {
      from: moment(new Date()).subtract(5, 'days').valueOf(),
      to: moment(new Date()).add(7, 'days').valueOf()
    }
  }

  return {
    from: dataRange.from,
    to: dataRange.to
  }
}

/**
 * Merges multiple terminalVisits into one combined terminal visit
 * ( strips off the orders, these can be merged / combined separately and then added to this visit )
 * @param {*} terminalVisits
 * @param {*} selectedShip
 */
export function mergeVisits(terminalVisits, selectedShip) {
  const firstVisit = terminalVisits[0]

  if (!firstVisit) {
    return null
  }

  let totalDelays = []
  let totalDemurrages = []

  terminalVisits.forEach(visit => {
    if (visit.delays && visit.delays.length > 0) {
      totalDelays = totalDelays.concat(visit.delays)
    }

    if (visit.demurrages && visit.demurrages.length > 0) {
      totalDemurrages = totalDemurrages.concat(visit.demurrages)
    }
  })

  const mergedVisit = {
    boardBoard: firstVisit.boardBoard,
    delays: totalDelays,
    demurrages: totalDemurrages,
    orders: [],
    ship: selectedShip,
    quayId: firstVisit.quayId,
    status: firstVisit.status,
    terminalId: firstVisit.terminalId,
    parallelGroups: firstVisit.parallelGroups,
    zIndex: 0,
    _id: uuidV4(),
    ignoreIMOS: firstVisit.ignoreIMOS
  }

  return mergedVisit
}

/**
 * Given a list of orders and a ship. Combine all the orders into one single order.
 * This order then has all the amounts of the products and various other properties combined.
 * @param {*} orders
 * @param {*} ship
 * @param {*} config
 */
export function mergeOrders(orders, ship, config) {
  let totalAmount = 0
  orders.forEach(order => {
    totalAmount += order.amount
  })

  const firstOrder = orders[0]

  if (!firstOrder) {
    return null
  }

  const mergedOrder = {
    administrativeInfo: {},
    amount: totalAmount,
    bollardEnd: firstOrder.bollardEnd,
    bollardStart: firstOrder.bollardStart,
    cargoEnd: 0,
    cargoStart: firstOrder.cargoStart,
    color: firstOrder.color,
    laycanStart: firstOrder.laycanStart,
    operation: firstOrder.operation,
    product: firstOrder.product,
    productGroup: firstOrder.productGroup,
    slackAfter: 0,
    slackPrior: 0,
    timeIsChained: true,
    uuid: uuidV4()
  }

  const updatedOrderWithCargoEndAndSlackTimes = updateDefaultSlack(updateCargoEnd(mergedOrder, config, 0), ship, config)

  return updatedOrderWithCargoEndAndSlackTimes
}

/**
 * Given an array of orders, check if all the orders have the same product
 * @param {*} orders
 */
export function checkIfProductsAreEqualForOrders(orders) {
  let areProductsEqual = true
  if (orders && !isEmpty(orders)) {
    const productToCheck = orders[0].product
    orders.forEach(order => {
      if (order.product !== productToCheck) {
        areProductsEqual = false
      }
    })
  }

  return areProductsEqual
}

/**
 * // goes through an array of supplied terminalVisits and returns only unique visits and orders
 * @param {*} selectedVisits
 */
export function getUniqueVisitsAndOrders(selectedVisits) {
  const clonedVisits = cloneDeep(selectedVisits)
  const visits = []
  const orders = []

  clonedVisits.forEach(visit => {
    visits.push(visit.referenceTerminalVisit)
    orders.push(...visit.referenceTerminalVisit.orders)
  })

  return {
    visits,
    orders
  }
}

/**
 *
 * @param {Config} config
 * @param {string} terminalId
 * @return {TerminalVisit} terminalId
 */
export function getNewEmptyVisit(config, terminalId) {
  const quayNumber = 0
  /** @type {Operation} */
  const operation = config.quays[quayNumber].defaultUnloading ? 'unloading' : 'loading'
  return {
    terminalId,
    // FIXME: support multiple quays?
    quayId: config.quays[quayNumber].name,
    status: 'planned',
    ship: {},
    orders: [
      {
        slackPrior: 0,
        slackAfter: 0,
        administrativeInfo: {},
        operation
      }
    ],
    delays: [],
    boardBoard: false,
    ignoreIMOS: true
  }
}

export function convertTerminalVisitToMovableVisit(terminalVisit, order, onExternalQuay) {
  // sanity check: order should be part of the terminal visit
  if (!terminalVisit.orders.find(o => o.uuid === order.uuid)) {
    console.error('Provided order not matching any of the orders in the terminal visit', {
      terminalVisit: terminalVisit,
      order: order
    })
  }

  const startTime = new Date(order.cargoStart)
  const endTime = new Date(order.cargoEnd)

  return {
    _id: terminalVisit._id,
    xStart: onExternalQuay ? 0.2 : order.bollardStart, // use bollards between 0 - 1 if it's a collapsedVisit
    xEnd: onExternalQuay ? 0.8 : order.bollardEnd, // use bollards between 0 - 1 if it's a collapsedVisit
    yStart: startTime,
    yEnd: endTime,
    slackPrior: order.slackPrior,
    slackAfter: order.slackAfter,
    referenceTerminalVisit: terminalVisit,
    referenceOrder: order,
    orderIndex: determineOrderNumber(terminalVisit, order.uuid)
  }
}

function isEqualBollards(objA, objB) {
  return objA.bollardStart === objB.bollardStart && objA.bollardEnd === objB.bollardEnd
}

export function convertExternTerminalVisitToMovableVisit(terminalVisit, order) {
  // sanity check: order should be part of the terminal visit
  if (!terminalVisit.orders.find(o => o.uuid === order.uuid)) {
    console.error('Provided order not matching any of the orders in the terminal visit', {
      terminalVisit: terminalVisit,
      order: order,
      result: terminalVisit.orders.find(o => o.uuid === order.uuid)
    })
  }
  const startTime = min(terminalVisit.orders.map(ord => ord.cargoStart))
  const endTime = max(terminalVisit.orders.map(ord => ord.cargoEnd))

  return {
    _id: terminalVisit._id,
    xStart: 0.2,
    xEnd: 0.8,
    yStart: new Date(startTime),
    yEnd: new Date(endTime),
    referenceTerminalVisit: cloneDeep(terminalVisit),
    referenceOrder: order
  }
}

export function separateVisitsWithUniqBollard(visit) {
  const sortedOrders = sortBy(visit.orders, ['cargoStart, cargoEnd'])

  const ordersByBollards = []
  for (const order of sortedOrders) {
    const lastGroup = last(ordersByBollards)
    const lastOrder = last(lastGroup)
    if (lastOrder && isEqualWith(order, lastOrder, isEqualBollards)) {
      lastGroup.push(order)
    } else {
      const newGroup = [order]
      ordersByBollards.push(newGroup)
    }
  }

  const visitPerBollard = ordersByBollards.map(ords => ({
    ...ords[0],
    cargoStart: min(ords.map(o => o.cargoStart)),
    cargoEnd: max(ords.map(o => o.cargoEnd))
  }))

  const lengthOfarr = visitPerBollard.length
  const visitStartTime = moment(getVisitStartTime(visit)).valueOf()
  const visitEndTime = moment(getVisitEndTime(visit)).valueOf()

  const renderShipdata = visitPerBollard.map((val, i) => {
    if (i === 0 && i === lengthOfarr - 1) {
      return {
        ...val,
        cargoStart: visitStartTime,
        cargoEnd: visitEndTime
      }
    }
    if (i === lengthOfarr - 1) {
      return {
        ...val,
        cargoEnd: visitEndTime
      }
    }
    if (i === 0) {
      return {
        ...val,
        cargoStart: visitStartTime,
        cargoEnd: visitPerBollard[1].cargoStart
      }
    }
    return {
      ...val,
      cargoEnd: visitPerBollard[i + 1].cargoStart
    }
  })
  return renderShipdata
}

export function disableShiftOrders(visit) {
  if (visit.status === VISIT_STATUSES.moored) {
    const minOrderStart = roundToMinutes(min(visit.orders.map(ord => ord.cargoStart)))
    const visitAta = roundToMinutes(visit.ataTime)
    return visitAta === minOrderStart
  }
  return true
}

export function disableMatchOrders(visit) {
  if (visit.status === VISIT_STATUSES.sailed) {
    const minOrderStart = roundToMinutes(min(visit.orders.map(ord => ord.cargoStart)))
    const maxOrderEnd = roundToMinutes(max(visit.orders.map(ord => ord.cargoEnd)))
    const visitAta = roundToMinutes(visit.ataTime)
    const visitAtd = roundToMinutes(visit.atdTime)
    return visitAta === minOrderStart && visitAtd === maxOrderEnd
  }
  return true
}
